import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { useTwoFactorChallenge } from '@/lib/auth';

export function TwoFactor() {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const challenge = useTwoFactorChallenge();
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await challenge.mutateAsync(useRecovery ? { recovery_code: code } : { code });
      navigate('/');
    } catch {
      setError('Invalid code.');
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold tracking-tight">Two-factor authentication</h1>
          <p className="mt-1 text-sm text-[var(--color-foreground-muted)]">
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>
        <form className="space-y-4" onSubmit={onSubmit}>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            placeholder={useRecovery ? 'Recovery code' : '123 456'}
            onChange={(e) => setCode(e.target.value)}
          />
          {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
          <Button type="submit" className="w-full" disabled={challenge.isPending}>
            {challenge.isPending ? 'Verifying…' : 'Verify'}
          </Button>
          <button
            type="button"
            onClick={() => setUseRecovery((v) => !v)}
            className="block w-full text-center text-xs text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]"
          >
            {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
          </button>
        </form>
      </div>
    </div>
  );
}
